<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { dateFormat } from '@/components/helpers/formats'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import CharityAvatar from '@/components/dashboard/ui/CharityAvatar.vue'
import type {
  RecurringDonationSeries,
  RecurringFrequency,
  RecurringStatus,
} from '@/types/recurringDonation'

const props = defineProps<{
  series: RecurringDonationSeries
}>()

const emit = defineEmits<{
  open: []
}>()

const { t } = useI18n()

const frequencyKey: Record<RecurringFrequency, string> = {
  weekly: 'Dashboard.Giving.AddDonationModal.FrequencyWeekly',
  monthly: 'Dashboard.Giving.AddDonationModal.FrequencyMonthly',
  quarterly: 'Dashboard.Giving.AddDonationModal.FrequencyQuarterly',
  annually: 'Dashboard.Giving.AddDonationModal.FrequencyAnnually',
}

const statusKey: Record<RecurringStatus, string> = {
  active: 'Dashboard.RecurringDonationModal.StatusActive',
  stopped: 'Dashboard.RecurringDonationModal.StatusStopped',
  completed: 'Dashboard.RecurringDonationModal.StatusCompleted',
}

/**
 * Charity stays on its own line. Cadence and the next, stopped, or completed
 * date sit underneath it, so a long name cannot run into the schedule.
 */
const scheduleDetail = (series: RecurringDonationSeries): string => {
  const frequency = t(frequencyKey[series.frequency])
  if (series.status === 'active' && series.nextDonationDate) {
    return t('Dashboard.Giving.RecurringList.Next', {
      frequency,
      date: dateFormat(series.nextDonationDate),
    })
  }
  if (series.status === 'stopped' && series.stoppedAt) {
    return t('Dashboard.Giving.RecurringList.Stopped', {
      frequency,
      date: dateFormat(series.stoppedAt),
    })
  }
  if (series.status === 'completed' && series.completedAt) {
    return t('Dashboard.Giving.RecurringList.Completed', {
      frequency,
      date: dateFormat(series.completedAt),
    })
  }
  return frequency
}
</script>

<template>
  <button
    type="button"
    class="sa-donation-row sa-donation-row--interactive sa-donation-row--series"
    @click="emit('open')"
  >
    <CharityAvatar :charity="series.charity" />
    <span class="sa-donation-main">
      <span class="sa-donation-charity">{{ series.charity }}</span>
      <span class="sa-donation-date">{{ scheduleDetail(props.series) }}</span>
    </span>
    <span class="sa-donation-right">
      <span class="sa-donation-amount">
        {{ formatMoneyFromPence(series.amountPence) }}
      </span>
      <span
        class="sa-recurring-status"
        :class="`sa-recurring-status--${series.status}`"
      >
        {{ t(statusKey[series.status]) }}
      </span>
    </span>
  </button>
</template>
